import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../services/supabase";
import { salesAPI, chatsAPI, favoritesAPI, reviewsAPI, productsAPI, setAuthToken, clearAuthToken } from "../services/api";
import bcrypt from "bcryptjs";

const AppContext = createContext();
const COMMISSION_RATE = 0.05;

export function AppProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("yad2yad_user")); } catch { return null; }
  });
  const [products,  setProducts]  = useState([]);
  const [sales,     setSales]     = useState([]);
  const [chats,     setChats]     = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [reviews,   setReviews]   = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    loadProducts();
    loadReviews();
    if (currentUser) { loadSales(); loadChats(); loadFavorites(); }
    setLoading(false);
  }, [currentUser?.id]);

  // Revisa ventas nuevas cada 10s, asi "Mis ventas" se actualiza sola sin recargar la pagina.
  useEffect(() => {
    if (!currentUser) return;
    const interval = setInterval(loadSales, 10000);
    return () => clearInterval(interval);
  }, [currentUser?.id]);

  const loadProducts = async () => {
    const { data } = await supabase.from("products").select("*").order("created_at", { ascending: false });
    if (data) setProducts(data.map(mapProduct));
  };

  const loadSales = async () => {
    if (!currentUser) return;
    try {
      const { sales: data } = currentUser.role === "admin" ? await salesAPI.getAll() : await salesAPI.getMine();
      if (data) setSales(data.map(mapSale));
    } catch(e) {}
  };

  const loadChats = async () => {
    try {
      const { chats: data } = await chatsAPI.getMine();
      if (data) setChats(data.map(mapChat));
    } catch(e) {}
  };

  const loadFavorites = async () => {
    try {
      const { productIds } = await favoritesAPI.getMine();
      if (productIds) setFavorites(productIds);
    } catch(e) {
      console.error("No se pudieron cargar los favoritos:", e);
    }
  };

  const loadReviews = async () => {
    const { data } = await supabase.from("reviews").select("*").order("created_at", { ascending: false });
    if (data) setReviews(data);
  };

  const mapProduct = (p) => ({
    id: p.id, sellerId: p.seller_id, sellerName: p.seller_name,
    title: p.title, description: p.description, price: p.price,
    color: p.color, quantity: p.quantity, category: p.category,
    zone: p.zone, shipping: p.shipping, shippingCost: p.shipping_cost || 0,
    shippingZoneCosts: p.shipping_zone_costs || {},
    colorStock: p.color_stock || {},
    estimatedDelivery: p.estimated_delivery || "",
    isInternational: p.is_international || false,
    paymentMethods: p.payment_methods || ["mercadopago"],
    images: p.images || [], extraFields: p.extra_fields || {},
    sold: p.sold, createdAt: p.created_at,
  });

  const mapSale = (s) => ({
    id: s.id, productId: s.product_id, productTitle: s.product_title,
    sellerId: s.seller_id, sellerName: s.seller_name,
    buyerId: s.buyer_id, buyerName: s.buyer_name,
    buyerDni: s.buyer_dni, buyerPhone: s.buyer_phone,
    cantidad: s.cantidad, pricePerUnit: s.price_per_unit,
    price: s.price, commission: s.commission, sellerReceives: s.seller_receives,
    shippingCost: s.shipping_cost || 0, shippingAddress: s.shipping_address, shippingPostalCode: s.shipping_postal_code,
    paymentMethod: s.payment_method, status: s.status, paymentStatus: s.payment_status,
    date: s.date, receivedDate: s.received_date,
    images: s.images || [], color: s.color,
    deliveryCode: s.delivery_code,
    deliveryCodeUsed: s.delivery_code_used,
    sellerPaid: s.seller_paid || false,
  });

  const mapChat = (c) => ({
    id: c.id, productId: c.product_id,
    productTitle: c.product_title, productPrice: c.product_price,
    sellerId: c.seller_id, sellerName: c.seller_name,
    buyerId: c.buyer_id, buyerName: c.buyer_name,
    createdAt: c.created_at, updatedAt: c.updated_at,
    messages: (c.messages || []).map(m => ({
      id: m.id, chatId: m.chat_id,
      senderId: m.sender_id, senderName: m.sender_name,
      text: m.text, read: m.read, date: m.date,
    })).sort((a,b) => new Date(a.date) - new Date(b.date)),
  });

  // ── Auth ─────────────────────────────────────────────────────────────────
   const login = async (email, password) => {
    const { authAPI } = await import("../services/api");
    const { user, token } = await authAPI.login(email, password);
    localStorage.setItem("yad2yad_user", JSON.stringify(user));
    setAuthToken(token);
    setCurrentUser(user);
    return user;
  };


  const logout = () => {
    import("../services/api").then(({ authAPI }) => authAPI.logout().catch(() => {}));
    localStorage.removeItem("yad2yad_user");
    clearAuthToken();
    setCurrentUser(null);
    setSales([]); setChats([]); setFavorites([]);
  };

  // Actualiza el usuario en memoria y en localStorage (ej: despues de editar el perfil)
  const updateCurrentUser = (changes) => {
    setCurrentUser(prev => {
      const updated = { ...prev, ...changes };
      localStorage.setItem("yad2yad_user", JSON.stringify(updated));
      return updated;
    });
  };


 const register = async (name, email, password, inviteCode, community, rabbi, dni) => {
    const { authAPI } = await import("../services/api");
    return await authAPI.register(name, email, password, inviteCode, community, rabbi, dni);
  };
 
  const verifyEmail = async (token) => {
    const { authAPI } = await import("../services/api");
    await authAPI.verifyEmail(token);
    localStorage.removeItem("yad2yad_user");
    clearAuthToken();
    setCurrentUser(null);
  };

  const setPassword = async (token, password) => {
    const { authAPI } = await import("../services/api");
    const { user, token: authToken } = await authAPI.setPassword(token, password);
    localStorage.setItem("yad2yad_user", JSON.stringify(user));
    setAuthToken(authToken);
    setCurrentUser(user);
    return user;
  };

  const forgotPassword = async (email) => {
    const { authAPI } = await import("../services/api");
    const { message } = await authAPI.forgotPassword(email);
    return message;
  };

  const resetPassword = async (token, password) => {
    const { authAPI } = await import("../services/api");
    const { user, token: authToken } = await authAPI.resetPassword(token, password);
    localStorage.setItem("yad2yad_user", JSON.stringify(user));
    setAuthToken(authToken);
    setCurrentUser(user);
    return user;
  };

  // ── Productos ─────────────────────────────────────────────────────────────
  const addProduct = async (formData) => {
    let data;
    try {
      ({ product: data } = await productsAPI.create(formData));
    } catch(e) {
      throw new Error("Error al publicar");
    }
    const mapped = mapProduct(data);
    setProducts(prev => [mapped, ...prev]);
    return mapped;
  };

  const updateProduct = async (id, formData) => {
    let data;
    try {
      ({ product: data } = await productsAPI.update(id, formData));
    } catch(e) {
      throw new Error("Error al actualizar");
    }
    const mapped = mapProduct(data);
    setProducts(prev => prev.map(p => p.id === id ? mapped : p));
    return mapped;
  };

  const deleteProduct = async (id) => {
    await productsAPI.remove(id);
    setProducts(prev => prev.filter(p => p.id !== id));
  };

  // ── Ventas ────────────────────────────────────────────────────────────────
  // Crea la venta en estado "pendiente de pago" y devuelve el link de Checkout de Mercado Pago.
  // El stock recien se descuenta cuando Mercado Pago confirma el pago (webhook en el backend).
  const startCheckout = async (productId, buyerId, buyerName, buyerDni, buyerPhone, cantidad = 1, buyerAddress = "", buyerPostalCode = "", buyerZone = "", buyerColor = "") => {
    const { initPoint } = await salesAPI.createPreference({ productId, buyerId, buyerName, buyerDni, buyerPhone, cantidad, buyerAddress, buyerPostalCode, buyerZone, buyerColor });
    return initPoint;
  };
  const confirmWithCode = async (saleId, code) => {
    const { receivedDate } = await salesAPI.confirmCode({ saleId, code });
    setSales(prev => prev.map(s =>
      s.id === saleId ? { ...s, status: "received", receivedDate, deliveryCodeUsed: true } : s
    ));
  };

  // ── Admin ─────────────────────────────────────────────────────────────────
  // Pasa siempre por el backend (protegido: solo funciona con sesion de admin), nunca directo a Supabase.
  const approveUser = async (userId) => {
    const { authAPI } = await import("../services/api");
    await authAPI.approveUser(userId);
  };

  const rejectUser = async (userId) => {
    const { authAPI } = await import("../services/api");
    await authAPI.rejectUser(userId);
  };

  // ── Reseñas ───────────────────────────────────────────────────────────────
  // La validacion real (que sea el comprador, que la entrega este confirmada, que no haya
  // calificado ya) se hace en el backend; esto de aca es solo para no hacer un pedido al pedo.
  const addReview = async (saleId, rating, comment) => {
    const sale = sales.find(s => s.id === saleId);
    if (!sale) throw new Error("Venta no encontrada");
    if (sale.status !== "received") throw new Error("Solo podes calificar despues de confirmar la entrega");
    const already = reviews.find(r => r.sale_id === saleId && r.buyer_id === currentUser.id);
    if (already) throw new Error("Ya calificaste esta compra");

    let data;
    try {
      ({ review: data } = await reviewsAPI.create({ saleId, rating, comment }));
    } catch(e) {
      throw new Error(e.message || "Error al guardar la calificacion");
    }
    setReviews(prev => [data, ...prev]);
    return data;
  };

  const getProductReviews = (productId) => reviews.filter(r => r.product_id === productId);
  const getSellerReviews = (sellerId) => reviews.filter(r => r.seller_id === sellerId);
  const getSellerRating  = (sellerId) => {
    const r = reviews.filter(r => r.seller_id === sellerId);
    if (r.length === 0) return null;
    return { average: parseFloat((r.reduce((a,b) => a + b.rating, 0) / r.length).toFixed(1)), total: r.length };
  };
  const hasReviewed = (saleId) => reviews.some(r => r.sale_id === saleId && r.buyer_id === currentUser?.id);

  // ── Favoritos ─────────────────────────────────────────────────────────────
  const toggleFavorite = async (productId) => {
    if (!currentUser) return;
    const isFav = favorites.includes(productId);
    try {
      await favoritesAPI.toggle(productId);
      setFavorites(prev => isFav ? prev.filter(id => id !== productId) : [...prev, productId]);
    } catch(e) {
      console.error("No se pudo actualizar el favorito:", e);
    }
  };
  const isFavorite     = (productId) => favorites.includes(productId);
  const getMyFavorites = () => products.filter(p => favorites.includes(p.id));

  // ── Chats ─────────────────────────────────────────────────────────────────
  const getOrCreateChat = async (productId, buyerId) => {
    const existing = chats.find(c => c.productId === productId && c.buyerId === buyerId);
    if (existing) return existing;
    try {
      const { chat } = await chatsAPI.getOrCreate(productId, buyerId);
      const newChat = mapChat(chat);
      setChats(prev => [newChat, ...prev]);
      return newChat;
    } catch(e) {
      return null;
    }
  };

  const sendMessage = async (chatId, text) => {
    if (!text.trim()) return;
    try {
      const { message: m } = await chatsAPI.sendMessage(chatId, text);
      const newMsg = { id: m.id, chatId: m.chat_id, senderId: m.sender_id, senderName: m.sender_name, text: m.text, read: m.read, date: m.date };
      setChats(prev => prev.map(c => c.id === chatId ? { ...c, messages: [...c.messages, newMsg], updatedAt: new Date().toISOString() } : c));
    } catch(e) {}
  };

  const markChatRead = async (chatId) => {
    try {
      await chatsAPI.markRead(chatId);
      setChats(prev => prev.map(c => {
        if (c.id !== chatId) return c;
        return { ...c, messages: c.messages.map(m => m.senderId !== currentUser?.id ? { ...m, read: true } : m) };
      }));
    } catch(e) {}
  };

  const getMyChats = () => {
    if (!currentUser) return [];
    return chats.filter(c => c.sellerId === currentUser.id || c.buyerId === currentUser.id)
      .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
  };

  const getUnreadCount = () => {
    if (!currentUser) return 0;
    return chats.reduce((acc, c) => {
      if (c.sellerId !== currentUser.id && c.buyerId !== currentUser.id) return acc;
      return acc + c.messages.filter(m => m.senderId !== currentUser.id && !m.read).length;
    }, 0);
  };

  const getAdminStats = () => {
    // Solo cuentan las ventas realmente pagadas por Mercado Pago (no carritos abandonados)
    const paid     = sales.filter(s => s.paymentStatus === "paid");
    const received = paid.filter(s => s.status === "received");
    const retained = paid.filter(s => s.status !== "received");
    return {
      totalCommission:   received.reduce((a, s) => a + s.commission, 0),
      pendingCommission: retained.reduce((a, s) => a + s.commission, 0),
      totalSales:        paid.length,
      totalVolume:       paid.reduce((a, s) => a + s.price, 0),
      pendingRetained:   retained.reduce((a, s) => a + s.price, 0),
    };
  };

  // Las compras/ventas que nunca se terminaron de pagar no se muestran — quedan invisibles hasta que Mercado Pago confirme el pago.
  const getMyPurchases = () => {
    if (!currentUser) return [];
    return sales.filter(s => s.buyerId === currentUser.id && s.paymentStatus === "paid").sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const getMySales = () => {
    if (!currentUser) return [];
    return sales.filter(s => s.sellerId === currentUser.id && s.paymentStatus === "paid").sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  return (
    <AppContext.Provider value={{
      currentUser, products, sales, chats, favorites, reviews, loading, COMMISSION_RATE,
      login, logout, register, verifyEmail, setPassword, updateCurrentUser, forgotPassword, resetPassword,
      addProduct, updateProduct, deleteProduct,
      startCheckout, confirmWithCode,
      addReview, getSellerReviews, getSellerRating, hasReviewed, getProductReviews,
      toggleFavorite, isFavorite, getMyFavorites,
      getOrCreateChat, sendMessage, markChatRead, getMyChats, getUnreadCount,
      getAdminStats, approveUser, rejectUser, getMyPurchases, getMySales,
      loadProducts, loadChats, loadSales,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);